import {Layers, RockBottomLayers, SimpleStageLayer} from "./RockBottomLayers";
import {RockBottomGameConfig} from "../RockBottomGameConfig";
import {RockBottom} from "../RockBottom";
import {gsap} from "gsap";
import {RockBottomSymbolState} from "../symbol/RockBottomSymbolConfig";
import {RockBottomSymbol} from "../symbol/RockBottomSymbol";
import {RockBottomParsedInitData} from "../server/data/RockBottomParsedInitData";
import {RockBottomParsedGameData} from "../server/data/RockBottomParsedGameData";
import {RockBottomSound} from "../sound/RockBottomSound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class RockBottomXSplitView extends PIXI.Container {
    private _layer: SimpleStageLayer | undefined;
    private _xSplitSpines: PIXI.spine.Spine[][] = [];
    private _isSkipped: boolean = false;
    private _prevSounds: string[] = [];

    constructor() {
        super();
        this.init();
    }

    private static checkIsMidSym(reelId: number, symId: number): boolean {
        //for non visible symbol on each reel with symId as -1
        if (symId === -1) {
            return false;
        }

        //for non visible symbol on reel 0 & 4 with symId as 3
        if (symId === 3 && (reelId === 0 || reelId === 4)) {
            return false;
        }

        const sym: RockBottomSymbol = RockBottom.reels[reelId].getSymbol(symId, false, false);
        return !!RockBottomGameConfig.MID_SYMS.includes(sym.symbolName);
    }

    private static checkIsLowSym(reelId: number, symId: number): boolean {
        //for non visible symbol on each reel with symId as -1
        if (symId === -1) {
            return false;
        }

        //for non visible symbol on reel 0 & 4 with symId as 3
        if (symId === 3 && (reelId === 0 || reelId === 4)) {
            return false;
        }

        const sym: RockBottomSymbol = RockBottom.reels[reelId].getSymbol(symId, false, false);
        return !!RockBottomGameConfig.LOW_SYMS.includes(sym.symbolName);
    }

    private static getCurrSymState(currSymSize: number): RockBottomSymbolState {
        let currState: RockBottomSymbolState;
        switch (currSymSize) {
            case 2:
                currState = RockBottomSymbolState.SPLIT_TO_2;
                break;
            case 4:
                currState = RockBottomSymbolState.SPLIT_TO_4;
                break;
            case 8:
                currState = RockBottomSymbolState.SPLIT_TO_8;
                break;
            default:
                currState = RockBottomSymbolState.NORMAL;
        }
        return currState;
    }

    private static getNextSymState(currSymSize: number): RockBottomSymbolState {
        let nextState: RockBottomSymbolState;
        switch (currSymSize) {
            case 1:
                nextState = RockBottomSymbolState.SPLIT_TO_2;
                break;
            case 2:
                nextState = RockBottomSymbolState.SPLIT_TO_4;
                break;
            case 4:
                nextState = RockBottomSymbolState.SPLIT_TO_8;
                break;
            default:
                nextState = RockBottomSymbolState.SPLIT_TO_2;
        }
        return nextState;
    }

    public playHighlightAnimation(reelId: number, symId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spineToPlay: PIXI.spine.Spine = this._xSplitSpines[reelId][symId],
            xSplitSym: RockBottomSymbol = RockBottom.reels[reelId].getSymbol(symId),
            animName: string = "Highlight",
            animDuration: number = spineToPlay.skeleton.data.findAnimation(animName).duration,
            firstXSIdxOnReel: number = (this.getXSplitPositions()).findIndex(n => n > -1);

        tl.to(this, {
            duration: animDuration,
            onStart: () => {
                if (!(RockBottom.isRestoredOnPick())) {
                    const splitSoundName: string = this.checkIfNoSplit(reelId, symId) ? RockBottomSound.XSPLIT_SPLITTING_ITSELF : RockBottomSound.XSPLIT_SPLITTING;

                    if (this.isSkipped()) {
                        if (reelId === firstXSIdxOnReel) {
                            this._prevSounds.push(splitSoundName);
                            RockBottomSound.soundPlayer.playTrack({name: splitSoundName});
                        } else {
                            if (!(this._prevSounds.includes(splitSoundName))) {
                                this._prevSounds.push(splitSoundName);
                                RockBottomSound.soundPlayer.playTrack({name: splitSoundName});
                            }
                        }
                    } else {
                        RockBottomSound.soundPlayer.playTrack({name: splitSoundName});
                    }
                }

                this.playSpine(spineToPlay, animName);
            },
            onComplete: () => {
                this.resetSpine(spineToPlay);
            }
        });
        tl.add(() => {
            xSplitSym.getStateAnimation(RockBottomSymbolState.SPLIT_TO_2);
        }, 3 / 30); // split on 3rd frame of highlight animation

        tl.add(this.splitMidSymbols(reelId, symId));

        return tl;
    }

    public abortXSplit(): void {
        this._isSkipped = true;
    }

    public isSkipped(): boolean {
        return (this._isSkipped || RockBottom.apiPlugin.settings.get(APISetting.FAST_SPIN));
    }

    public resetVariable(): void {
        this._isSkipped = false;
        this._prevSounds = [];
    }

    private checkIfNoSplit(reelId: number, symId: number): boolean {
        let isSplit: boolean = true;

        let parsedSymSize: number[][] = RockBottom.model.parsedGameData.symbolSizes;
        if (RockBottom.isRestoredOnPick()) {
            parsedSymSize = RockBottom.model.parsedInitData.symbolSizes;
        }

        for (let i: number = 0; i < RockBottomGameConfig.REELS_NUM; i++) {
            if (i !== reelId) {
                if ((i === 0 || i === 4) && (symId === 1 || symId === 2)) {
                    if (!((RockBottom.reels[i].getSymbol(symId - 1).symbolName === "XS") ||
                        (RockBottom.reels[i].getSymbol(symId).symbolName === "XS"))) {
                        if (parsedSymSize[i][symId - 1] > 1 || parsedSymSize[i][symId] > 1) {
                            isSplit = false;
                            break;
                        }
                    }
                } else if ((i === 0 || i === 4) && (symId === 3)) {
                    if (RockBottom.reels[i].getSymbol(symId - 1).symbolName !== "XS") {
                        if (parsedSymSize[i][symId - 1] > 1) {
                            isSplit = false;
                            break;
                        }
                    }
                } else {
                    if (RockBottom.reels[i].getSymbol(symId).symbolName !== "XS") {
                        if (parsedSymSize[i][symId] > 1) {
                            isSplit = false;
                            break;
                        }
                    }
                }
            }
        }

        return isSplit;
    }

    private getXSplitPositions(): number[] {
        const xSplitPos: number[] = [],
            parsedData: RockBottomParsedInitData | RockBottomParsedGameData = RockBottom.getParsedData();

        for (let reelId: number = 0; reelId < parsedData.reels.length; reelId++) {
            const parsedReels: string[] = parsedData.reels[reelId].slice(1, -1);
            xSplitPos.push(parsedReels.indexOf("XS"));
        }

        return xSplitPos;
    }

    private splitMidSymbols(reelId: number, symId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let currReelId: number = 0; currReelId < RockBottomGameConfig.REELS_NUM; currReelId++) {
            if (currReelId !== reelId) {
                for (let j: number = 0; j < 2; j++) {
                    let currSymId: number;
                    if (currReelId === 0 || currReelId === 4) {
                        currSymId = symId - j;
                    } else {
                        if (j === 1) {
                            break;
                        }
                        currSymId = symId;
                    }
                    if (RockBottomXSplitView.checkIsMidSym(currReelId, currSymId)) {
                        const animName: string = currReelId < reelId ? "right_to_Left" : "Left_to_right";
                        tl.add(this.playSymSplitAnim(currReelId, currSymId, animName), 0);
                    } else if (RockBottomXSplitView.checkIsLowSym(currReelId, currSymId)) {
                        const animName: string = currReelId < reelId ? "Low_Sym_right_to_Left" : "Low_Sym_Left_to_right";
                        if (this.checkIfNoSplit(reelId, symId)) {
                            this.playSymSplitAnim(currReelId, currSymId, animName);
                        } else {
                            tl.add(this.playSymSplitAnim(currReelId, currSymId, animName), 0);
                        }
                    } else {
                        //do nothing because this is xSplit symbol or scatter
                    }
                }
            }
        }
        return tl;
    }

    private playSymSplitAnim(reelId: number, symId: number, animName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spineToPlay: PIXI.spine.Spine = this._xSplitSpines[reelId][symId],
            animDuration: number = spineToPlay.skeleton.data.findAnimation(animName).duration;

        tl.to(this, {
            duration: animDuration,
            onStart: () => {
                this.playSpine(spineToPlay, animName);
            },
            onComplete: () => {
                this.resetSpine(spineToPlay);
            }
        });

        if (animName.indexOf("Low_Sym") === -1) {
            tl.add(() => {
                const parsedData: RockBottomParsedInitData | RockBottomParsedGameData = RockBottom.getParsedData(),
                    currSym: RockBottomSymbol = RockBottom.reels[reelId].getSymbol(symId);

                let parsedSymSize: number,
                    symState: RockBottomSymbolState = RockBottomXSplitView.getNextSymState(currSym.currSymbolSize);

                if (RockBottom.isRestoredOnPick()) {
                    parsedSymSize = RockBottom.model.parsedInitData.symbolSizes[reelId][symId];
                } else {
                    parsedSymSize = parsedData.symbolSizes[reelId][symId];
                }

                if (this.isSkipped()) {
                    currSym.getStateAnimation(RockBottomXSplitView.getCurrSymState(parsedSymSize));
                } else {
                    if (currSym.currSymbolSize < parsedSymSize && RockBottomGameConfig.MID_SYMS.includes(currSym.symbolName)) {
                        currSym.getStateAnimation(symState);
                    }
                }

            }, (this.isSkipped() ? 0 : (9 / 30))); // split on 9th frame of break animation*/
        }

        return tl;
    }

    private init(): void {
        this._layer = RockBottomLayers.getLayer(Layers.XSPLIT_VIEW);
        this.resetVariable();
        this.createSpine();
        this._layer?.addChild(this);
    }

    private resetSpine(spineObj: PIXI.spine.Spine): void {
        spineObj.visible = false;
        spineObj.updateTransform();
        spineObj.state.clearTracks();
        spineObj.skeleton.setToSetupPose();
        spineObj.skeleton.setSlotsToSetupPose();
        spineObj.state.setEmptyAnimation(0, 0);
    }

    private playSpine(spineObj: PIXI.spine.Spine, animName: string, isLooped: boolean = false): void {
        spineObj.visible = true;
        spineObj.state.setAnimation(0, animName, isLooped);
    }

    private createSpine(): void {
        for (let reelId: number = 0; reelId < RockBottomGameConfig.REELS_NUM; reelId++) {
            this._xSplitSpines[reelId] = [];
            for (let symId: number = 0; symId < RockBottomGameConfig.SYMS_PER_REEL[reelId]; symId++) {
                const spineObj: PIXI.spine.Spine = new PIXI.spine.Spine(RockBottom.assetLoader.getSpineAsset("xSplit")),
                    currSymbol = RockBottom.reels[reelId].getSymbol(symId),
                    currSymbolPos: PIXI.Point = currSymbol.getGlobalPosition();

                spineObj.position.set(currSymbolPos.x, currSymbolPos.y);
                spineObj.name = "xSplit_" + reelId + "_" + symId;
                this._xSplitSpines[reelId].push(spineObj);
                this.addChild(spineObj);
                this.resetSpine(spineObj);
            }
        }
    }
}